import {
  type BackgroundProps,
  Box,
  Center,
  GridItem,
  type GridProps,
  Heading,
  type ImgProps,
  Stack,
  Text,
} from '@chakra-ui/react'

import FromTheFounderOfKhanAcademy from '@/components/About/FromTheFounderOfKhanAcademy'
import ShwGrid from '@/components/Layout/ShwGrid'
import PublicImage from '@/components/PublicImage/PublicImage'
import TextWithLines from '@/components/TextWithLines/TextWithLines'

import AlertWithCTA, { type AlertWithCTAProps } from './AlertWithCTA'
import CTAButton from './CTAButton/CTAButton'
import type { CTAWithoutVariant } from './CTAButton/types'
import Section from './Section'

type Graphic = {
  src: string
  alt: string
  borderRadius?: ImgProps['borderRadius']
  caption?: string
  maxWidth?: string
}

export default function Hero({
  includeKAFounder = false,
  aboveTitle,
  title,
  subtitle,
  description,
  primaryCTA,
  secondaryCTA,
  graphic,
  bg,
  alertWithCTA,
  ctaHeader,
  ctaFooter,
  banner,
  alignGridItems = 'center',
  coverViewport = false,
  showGraphicOnMobile = false,
  reverseGraphicOnMobile = false,
}: {
  includeKAFounder?: boolean
  aboveTitle?: () => JSX.Element
  title: string | (() => JSX.Element)
  subtitle?: string
  description?: string | (() => JSX.Element) | null
  primaryCTA?: CTAWithoutVariant | (() => JSX.Element)
  secondaryCTA?: CTAWithoutVariant
  graphic?: Graphic
  bg?: BackgroundProps['bg']
  ctaHeader?: (() => JSX.Element | null) | null
  ctaFooter?: (() => JSX.Element | null) | null
  alertWithCTA?: AlertWithCTAProps
  banner?: (() => JSX.Element) | null
  alignGridItems?: GridProps['alignItems']
  coverViewport?: boolean
  showGraphicOnMobile?: boolean
  reverseGraphicOnMobile?: boolean
}): JSX.Element {
  const isSingleColumn = graphic == null
  const align = isSingleColumn ? 'center' : 'left'

  return (
    <Section bg={bg} py={4} coverViewport={coverViewport}>
      {banner != null ? banner() : null}
      <ShwGrid alignItems={alignGridItems}>
        {alertWithCTA != null ? (
          <GridItem colSpan={12}>
            <AlertWithCTA {...alertWithCTA} />
          </GridItem>
        ) : null}
        <GridItem
          colSpan={isSingleColumn ? 12 : [12, 12, 12, 6]}
          gridRow={reverseGraphicOnMobile ? { base: 2, lg: 1 } : undefined}
        >
          <Stack direction="column" spacing="24px" align={align}>
            {aboveTitle != null ? <Box>{aboveTitle()}</Box> : null}
            {includeKAFounder ? <FromTheFounderOfKhanAcademy /> : null}
            {typeof title === 'function' ? (
              title()
            ) : (
              <Heading size="3xl" as="h1" textAlign={align}>
                {title}
              </Heading>
            )}
            {subtitle != null ? (
              <Heading size="lg" as="h4" textAlign={align}>
                {subtitle}
              </Heading>
            ) : null}
            {description != null ? (
              <Stack direction="column" spacing={2} align={align}>
                {typeof description === 'function' ? (
                  description()
                ) : (
                  <TextWithLines textAlign={align} text={description} />
                )}
              </Stack>
            ) : null}
            <Stack direction="column" spacing="8px">
              {ctaHeader == null ? null : <Box>{ctaHeader()}</Box>}
              <Stack direction={['column', 'row']} spacing="8px">
                {primaryCTA == null ? null : typeof primaryCTA === 'function' ? (
                  primaryCTA()
                ) : (
                  <CTAButton variant="primary" {...primaryCTA} />
                )}
                {secondaryCTA != null ? <CTAButton variant="secondary" {...secondaryCTA} /> : null}
              </Stack>
              {ctaFooter == null ? null : <Box>{ctaFooter()}</Box>}
            </Stack>
          </Stack>
        </GridItem>
        {isSingleColumn ? null : (
          <GridItem
            colSpan={[12, 12, 12, 6]}
            display={showGraphicOnMobile ? 'flex' : ['none', 'none', 'none', 'flex']}
            alignSelf="center"
            mb={reverseGraphicOnMobile && showGraphicOnMobile ? { base: '24px', md: 0 } : undefined}
          >
            <Stack direction="column" spacing={4} width="100%">
              <Center>
                <PublicImage
                  src={graphic.src}
                  alt={graphic.alt}
                  maxWidth={graphic.maxWidth ?? '100%'}
                  borderRadius={graphic.borderRadius}
                />
              </Center>
              {graphic.caption != null ? (
                <Text variant="supporting" color="fg.subtle">
                  {graphic.caption}
                </Text>
              ) : null}
            </Stack>
          </GridItem>
        )}
      </ShwGrid>
    </Section>
  )
}
